import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  addEdge, applyNodeChanges, applyEdgeChanges,
  Background, Controls, MiniMap,
  MarkerType, Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useNavigate, useParams } from 'react-router-dom';
import { authAxios, API } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { nodeTypes, nodeColors, nodeLabels } from './FlowNodes';
import PropertiesPanel from './PropertiesPanel';
import {
  Save, Loader2, ArrowLeft, Play, Pause, Copy, Trash2, LayoutGrid,
  Zap, MessageSquare, Image, Mic, Video, FileText, List, Clock, GitBranch, StopCircle,
  AlertTriangle, CheckCircle, FlaskConical, X, ChevronRight,
} from 'lucide-react';

const sidebarNodes = [
  { type: 'trigger', label: 'Trigger', icon: Zap, color: '#22c55e' },
  { type: 'text_message', label: 'Text Message', icon: MessageSquare, color: '#0f172a' },
  { type: 'image_message', label: 'Image', icon: Image, color: '#3b82f6' },
  { type: 'audio_message', label: 'Audio', icon: Mic, color: '#8b5cf6' },
  { type: 'video_message', label: 'Video', icon: Video, color: '#f97316' },
  { type: 'document_message', label: 'Document/PDF', icon: FileText, color: '#ef4444' },
  { type: 'quick_reply', label: 'Quick Reply', icon: List, color: '#06b6d4' },
  { type: 'delay', label: 'Delay', icon: Clock, color: '#eab308' },
  { type: 'condition', label: 'Condition', icon: GitBranch, color: '#ec4899' },
  { type: 'end', label: 'End', icon: StopCircle, color: '#64748b' },
];

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: { stroke: '#475569', strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#475569' },
};

// ==================== VALIDATION ====================
function validateFlow(nodes, edges) {
  const warnings = [];
  const errors = [];
  const triggers = nodes.filter(n => n.type === 'trigger');
  if (triggers.length === 0) errors.push('Flow must have at least one Trigger node');
  if (triggers.length > 1) warnings.push('Multiple trigger nodes found — only the first will be used');
  triggers.forEach(t => {
    const cfg = t.data?.config || {};
    if (cfg.trigger_type === 'keyword' && !cfg.keyword?.trim()) errors.push('Trigger keyword is empty');
  });
  const connectedIds = new Set();
  edges.forEach(e => { connectedIds.add(e.source); connectedIds.add(e.target); });
  const disconnected = nodes.filter(n => !connectedIds.has(n.id) && nodes.length > 1);
  if (disconnected.length > 0) warnings.push(`${disconnected.length} disconnected node(s): ${disconnected.map(n => n.data.title).join(', ')}`);
  nodes.forEach(n => {
    const cfg = n.data?.config || {};
    if (n.type === 'text_message' && !cfg.content?.trim()) warnings.push(`"${n.data.title}" has no message text`);
    if (['image_message', 'audio_message', 'video_message', 'document_message'].includes(n.type) && !cfg.media_url?.trim()) errors.push(`"${n.data.title}" has no media source`);
    if (n.type === 'quick_reply') {
      const opts = cfg.options || [];
      if (opts.length === 0) errors.push(`"${n.data.title}" has no options`);
      opts.forEach((o, i) => { if (!o.label?.trim()) warnings.push(`"${n.data.title}" option ${i + 1} has no label`); });
      const outEdges = edges.filter(e => e.source === n.id);
      const unlinked = opts.filter(o => !outEdges.find(e => e.sourceHandle === o.id));
      if (unlinked.length > 0) warnings.push(`"${n.data.title}" has ${unlinked.length} option(s) without connected branch`);
    }
  });
  const hasEnd = nodes.some(n => n.type === 'end');
  if (!hasEnd && nodes.length > 1) warnings.push('No End node — flow may not terminate cleanly');
  return { errors, warnings, valid: errors.length === 0 };
}

// ==================== TEST SIMULATOR ====================
function TestPanel({ nodes, edges, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [currentNodeId, setCurrentNodeId] = useState(null);
  const [finished, setFinished] = useState(false);
  const scrollRef = useRef(null);

  const edgeMap = useRef({});
  useEffect(() => {
    const m = {};
    edges.forEach(e => { if (!m[e.source]) m[e.source] = []; m[e.source].push(e); });
    edgeMap.current = m;
  }, [edges]);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const getNode = (id) => nodes.find(n => n.id === id);
  const getNextNodeId = (nodeId, handleId) => {
    const outs = edgeMap.current[nodeId] || [];
    if (handleId) { const e = outs.find(e => e.sourceHandle === handleId); return e?.target; }
    return outs[0]?.target || null;
  };

  const executeNode = useCallback((nodeId) => {
    const node = getNode(nodeId);
    if (!node) { setFinished(true); return; }
    const cfg = node.data?.config || {};
    setCurrentNodeId(nodeId);

    if (node.type === 'end') {
      if (cfg.message) setMessages(p => [...p, { from: 'bot', text: cfg.message, nodeTitle: node.data.title }]);
      setMessages(p => [...p, { from: 'system', text: 'Flow ended' }]);
      setFinished(true); return;
    }
    if (node.type === 'trigger') {
      const nextId = getNextNodeId(nodeId);
      if (nextId) executeNode(nextId);
      else { setFinished(true); }
      return;
    }
    if (['text_message', 'image_message', 'audio_message', 'video_message', 'document_message'].includes(node.type)) {
      const text = cfg.content || cfg.media_url || `[${node.type}]`;
      setMessages(p => [...p, { from: 'bot', text, nodeTitle: node.data.title, nodeType: node.type }]);
      const nextId = getNextNodeId(nodeId);
      if (nextId) { setTimeout(() => executeNode(nextId), 300); }
      else { setFinished(true); }
      return;
    }
    if (node.type === 'quick_reply') {
      const opts = cfg.options || [];
      let text = cfg.content || 'Choose an option:';
      text += '\n' + opts.map((o, i) => `${i + 1}. ${o.label}`).join('\n');
      setMessages(p => [...p, { from: 'bot', text, nodeTitle: node.data.title, waitingForInput: true, options: opts }]);
      return; // Wait for user input
    }
    if (node.type === 'delay') {
      setMessages(p => [...p, { from: 'system', text: `Delay: ${cfg.delay_amount || 5} ${cfg.delay_unit || 'seconds'}` }]);
      const nextId = getNextNodeId(nodeId);
      if (nextId) setTimeout(() => executeNode(nextId), 500);
      else setFinished(true);
      return;
    }
    // Default
    const nextId = getNextNodeId(nodeId);
    if (nextId) executeNode(nextId); else setFinished(true);
  }, [nodes]);

  const startTest = () => {
    setMessages([]); setFinished(false); setCurrentNodeId(null);
    const trigger = nodes.find(n => n.type === 'trigger');
    if (!trigger) { setMessages([{ from: 'system', text: 'No trigger node found!' }]); return; }
    setMessages([{ from: 'system', text: 'Flow started' }]);
    executeNode(trigger.id);
  };

  const sendInput = () => {
    if (!input.trim() || finished) return;
    setMessages(p => [...p, { from: 'user', text: input }]);
    const lastBotMsg = [...messages].reverse().find(m => m.waitingForInput);
    if (lastBotMsg && lastBotMsg.options) {
      const matched = lastBotMsg.options.find(o => o.value === input.trim() || o.label.toLowerCase() === input.trim().toLowerCase() || o.value === input.trim());
      if (matched) {
        const nextId = getNextNodeId(currentNodeId, matched.id);
        if (nextId) { setInput(''); setTimeout(() => executeNode(nextId), 200); return; }
      }
      setMessages(p => [...p, { from: 'system', text: 'No matching option. Try again.' }]);
    }
    setInput('');
  };

  return (
    <div className="w-80 bg-[#0f172a] border-l border-slate-700 flex flex-col h-full" data-testid="test-panel">
      <div className="p-3 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-[#22c55e]" />
          <span className="text-sm font-bold text-white">Test Flow</span>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={startTest} data-testid="restart-test"><Play className="w-3 h-3" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
      </div>
      <ScrollArea className="flex-1 p-3">
        <div className="space-y-2">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <FlaskConical className="w-8 h-8 mx-auto mb-2 text-slate-600" />
              <p className="text-xs text-slate-500">Click play to simulate your flow</p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : msg.from === 'system' ? 'justify-center' : 'justify-start'}`}>
              {msg.from === 'system' ? (
                <span className="text-[10px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded">{msg.text}</span>
              ) : (
                <div className={`max-w-[85%] rounded-lg px-3 py-2 ${msg.from === 'bot' ? 'bg-slate-800 text-white' : 'bg-[#22c55e] text-white'}`}>
                  {msg.nodeTitle && <p className="text-[9px] opacity-60 mb-0.5">{msg.nodeTitle}</p>}
                  <p className="text-xs whitespace-pre-wrap">{msg.text}</p>
                </div>
              )}
            </div>
          ))}
          <div ref={scrollRef} />
        </div>
      </ScrollArea>
      <div className="p-2 border-t border-slate-700 flex gap-1">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendInput()} placeholder="Type response..." className="bg-slate-800 border-slate-600 text-white text-xs h-8 flex-1" disabled={finished && messages.length > 0} data-testid="test-input" />
        <Button size="sm" className="h-8 bg-[#22c55e] text-white" onClick={sendInput} disabled={finished && messages.length > 0}><ChevronRight className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function VisualFlowBuilder() {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [flowName, setFlowName] = useState('New Flow');
  const [triggerKeyword, setTriggerKeyword] = useState('');
  const [anyMessageTrigger, setAnyMessageTrigger] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!flowId);
  const [media, setMedia] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [validation, setValidation] = useState({ errors: [], warnings: [], valid: true });
  const savedStateRef = useRef('');

  // Load flow and media
  useEffect(() => {
    const loadData = async () => {
      try {
        const mediaRes = await authAxios.get(`${API}/media`).catch(() => ({ data: [] }));
        setMedia(mediaRes.data);
        if (flowId) {
          const { data } = await authAxios.get(`${API}/visual-flows/${flowId}`);
          setFlowName(data.name || 'Untitled');
          setTriggerKeyword(data.trigger_keyword || '');
          setAnyMessageTrigger(data.any_message_trigger || false);
          setIsActive(data.is_active !== false);
          const loadedNodes = (data.nodes || []).map(n => ({
            id: n.id, type: n.node_type,
            position: { x: n.position_x || 0, y: n.position_y || 0 },
            data: { node_type: n.node_type, title: n.title, config: n.config || {} },
          }));
          const loadedEdges = (data.edges || []).map(e => ({
            id: e.id, source: e.source, target: e.target,
            sourceHandle: e.source_handle || undefined, targetHandle: e.target_handle || undefined,
            ...defaultEdgeOptions,
          }));
          setNodes(loadedNodes);
          setEdges(loadedEdges);
          savedStateRef.current = JSON.stringify({ nodes: loadedNodes.map(n => ({ id: n.id, data: n.data })), edges: loadedEdges.map(e => ({ id: e.id, source: e.source, target: e.target })) });
          if (data.viewport && data.viewport.x !== undefined) {
            setTimeout(() => { if (reactFlowInstance) reactFlowInstance.setViewport(data.viewport); }, 200);
          }
        }
      } catch (err) { console.error('Load error:', err); }
      finally { setLoading(false); }
    };
    loadData();
  }, [flowId]);

  // Track unsaved changes
  useEffect(() => {
    const currentState = JSON.stringify({ nodes: nodes.map(n => ({ id: n.id, data: n.data })), edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target })) });
    setHasUnsaved(savedStateRef.current !== '' && currentState !== savedStateRef.current);
  }, [nodes, edges]);

  // Live validation
  useEffect(() => { setValidation(validateFlow(nodes, edges)); }, [nodes, edges]);

  const pushHistory = useCallback(() => {
    const state = { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) };
    setHistory(prev => [...prev.slice(0, historyIdx + 1), state].slice(-30));
    setHistoryIdx(prev => prev + 1);
  }, [nodes, edges, historyIdx]);

  const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);
  const onConnect = useCallback((connection) => {
    pushHistory();
    setEdges((eds) => addEdge({ ...connection, ...defaultEdgeOptions, id: `e_${Date.now()}` }, eds));
  }, [pushHistory]);

  const onNodeClick = useCallback((_, node) => { setSelectedNode(node); setShowTest(false); }, []);
  const onPaneClick = useCallback(() => setSelectedNode(null), []);
  const onDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/reactflow');
    if (!type || !reactFlowInstance) return;
    const position = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const newId = `${type}_${Date.now()}`;
    const defaultConfig = {};
    if (type === 'trigger') defaultConfig.trigger_type = 'keyword';
    if (type === 'quick_reply') defaultConfig.options = [{ id: `opt_${Date.now()}`, label: 'Option 1', value: '1' }];
    if (type === 'condition') defaultConfig.branches = [{ id: `br_${Date.now()}`, label: 'Match', type: 'equals', value: '' }];
    if (type === 'delay') { defaultConfig.delay_amount = 5; defaultConfig.delay_unit = 'seconds'; }
    pushHistory();
    setNodes((nds) => [...nds, { id: newId, type, position, data: { node_type: type, title: nodeLabels[type] || 'Node', config: defaultConfig } }]);
  }, [reactFlowInstance, pushHistory]);

  const onDragStart = (e, nodeType) => { e.dataTransfer.setData('application/reactflow', nodeType); e.dataTransfer.effectAllowed = 'move'; };

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes((nds) => nds.map((n) => n.id === nodeId ? { ...n, data: newData } : n));
    setSelectedNode((prev) => prev && prev.id === nodeId ? { ...prev, data: newData } : prev);
  }, []);

  const deleteSelectedNode = useCallback(() => {
    if (!selectedNode) return;
    const outEdges = edges.filter(e => e.source === selectedNode.id);
    if (outEdges.length > 0 && !window.confirm(`This node has ${outEdges.length} outgoing connection(s). Delete anyway?`)) return;
    pushHistory();
    setNodes((nds) => nds.filter(n => n.id !== selectedNode.id));
    setEdges((eds) => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setSelectedNode(null);
  }, [selectedNode, pushHistory, edges]);

  const duplicateSelectedNode = useCallback(() => {
    if (!selectedNode) return;
    const newId = `${selectedNode.type}_${Date.now()}`;
    pushHistory();
    setNodes((nds) => [...nds, {
      ...selectedNode, id: newId,
      position: { x: selectedNode.position.x + 30, y: selectedNode.position.y + 30 },
      data: { ...JSON.parse(JSON.stringify(selectedNode.data)), title: `${selectedNode.data.title} (copy)` },
    }]);
  }, [selectedNode, pushHistory]);

  const autoLayout = useCallback(() => {
    const trigger = nodes.find(n => n.type === 'trigger');
    if (!trigger) return;
    pushHistory();
    const visited = new Set();
    const positions = {};
    let orphanY = 0;
    const em = {};
    edges.forEach(e => { if (!em[e.source]) em[e.source] = []; em[e.source].push(e.target); });
    const queue = [[trigger.id, 0]];
    const rows = {};
    while (queue.length) {
      const [id, depth] = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);
      if (!rows[depth]) rows[depth] = [];
      rows[depth].push(id);
      (em[id] || []).forEach(tid => { if (!visited.has(tid)) queue.push([tid, depth + 1]); });
    }
    Object.entries(rows).forEach(([depth, ids]) => {
      ids.forEach((id, idx) => { positions[id] = { x: 300 + idx * 300, y: parseInt(depth) * 160 }; });
    });
    nodes.forEach(n => { if (!visited.has(n.id)) { positions[n.id] = { x: 700, y: orphanY }; orphanY += 160; } });
    setNodes(nds => nds.map(n => positions[n.id] ? { ...n, position: positions[n.id] } : n));
  }, [nodes, edges, pushHistory]);

  const undo = useCallback(() => {
    if (historyIdx < 0) return;
    const state = history[historyIdx];
    if (state) { setNodes(state.nodes); setEdges(state.edges); setHistoryIdx(prev => prev - 1); }
  }, [history, historyIdx]);

  const handleSave = async () => {
    if (validation.errors.length > 0) { setShowValidation(true); return; }
    setSaving(true);
    try {
      // === PRE-SAVE SANITIZATION ===
      // 1. Ensure all nodes have valid IDs
      const seenIds = new Set();
      const sanitizedNodes = nodes.map(n => {
        let id = n.id;
        if (!id || seenIds.has(id)) { id = `${n.type || 'node'}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`; }
        seenIds.add(id);
        const posX = typeof n.position?.x === 'number' && isFinite(n.position.x) ? n.position.x : 0;
        const posY = typeof n.position?.y === 'number' && isFinite(n.position.y) ? n.position.y : 0;
        return { id, node_type: n.type || 'text_message', title: (n.data?.title || '').substring(0, 500), position_x: posX, position_y: posY, config: n.data?.config || {} };
      });

      // 2. Clean edges: remove orphans, ensure valid refs
      const validNodeIds = new Set(sanitizedNodes.map(n => n.id));
      const sanitizedEdges = edges
        .filter(e => e.source && e.target && validNodeIds.has(e.source) && validNodeIds.has(e.target))
        .map(e => ({ id: e.id || `e_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, source: e.source, target: e.target, source_handle: e.sourceHandle || null, target_handle: e.targetHandle || null }));

      // 3. Extract trigger info
      const triggerNode = sanitizedNodes.find(n => n.node_type === 'trigger');
      const tk = triggerNode?.config?.keyword || triggerKeyword || '';
      const amt = triggerNode?.config?.trigger_type === 'any_message' || anyMessageTrigger;

      const payload = {
        name: flowName || 'Untitled Flow',
        trigger_keyword: tk,
        any_message_trigger: amt,
        is_active: isActive,
        nodes: sanitizedNodes,
        edges: sanitizedEdges,
        viewport: reactFlowInstance ? reactFlowInstance.getViewport() : {},
      };

      if (flowId) {
        await authAxios.put(`${API}/visual-flows/${flowId}`, payload);
      } else {
        const { data } = await authAxios.post(`${API}/visual-flows`, payload);
        navigate(`/dashboard/flow-builder/${data.id}`, { replace: true });
      }
      savedStateRef.current = JSON.stringify({ nodes: nodes.map(n => ({ id: n.id, data: n.data })), edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target })) });
      setHasUnsaved(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let msg = 'Save failed';
      if (detail) { msg = typeof detail === 'string' ? detail : JSON.stringify(detail); }
      else if (status === 413) { msg = 'Flow is too large. Try reducing the number of nodes.'; }
      else if (status === 422) { msg = 'Invalid flow data. Check node configurations.'; }
      else if (err.message) { msg = err.message; }
      alert(`Save error: ${msg}`);
    } finally { setSaving(false); }
  };

  const handleDuplicate = async () => {
    if (!flowId) return;
    try { const { data } = await authAxios.post(`${API}/visual-flows/${flowId}/duplicate`); navigate(`/dashboard/flow-builder/${data.id}`); }
    catch { alert('Duplicate failed'); }
  };

  const handleDeleteFlow = async () => {
    if (!flowId || !window.confirm('Delete this entire flow?')) return;
    try { await authAxios.delete(`${API}/flows/${flowId}`); navigate('/dashboard/bot-studio'); }
    catch { alert('Delete failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-slate-900"><Loader2 className="w-8 h-8 text-white animate-spin" /></div>;

  const selectedNodeFull = selectedNode ? nodes.find(n => n.id === selectedNode.id) : null;

  return (
    <div className="h-screen flex flex-col bg-slate-900" data-testid="visual-flow-builder">
      {/* Top Toolbar */}
      <div className="h-12 bg-[#0f172a] border-b border-slate-700 flex items-center px-3 gap-2 shrink-0" data-testid="flow-toolbar">
        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white" onClick={() => navigate('/dashboard/bot-studio')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="h-5 w-px bg-slate-700" />
        <Input value={flowName} onChange={(e) => setFlowName(e.target.value)} className="bg-slate-800 border-slate-600 text-white text-sm h-8 w-48" data-testid="flow-name-input" />
        {hasUnsaved && <span className="text-[10px] text-amber-400 font-medium" data-testid="unsaved-indicator">unsaved</span>}
        <div className="h-5 w-px bg-slate-700" />
        <Button size="sm" variant={isActive ? "default" : "outline"} className={`h-7 text-xs ${isActive ? 'bg-[#22c55e] hover:bg-[#16a34a] text-white' : 'border-slate-600 text-slate-400'}`} onClick={() => setIsActive(!isActive)} data-testid="toggle-active">
          {isActive ? <><Play className="w-3 h-3 mr-1" />Active</> : <><Pause className="w-3 h-3 mr-1" />Inactive</>}
        </Button>
        {/* Validation status */}
        <Button size="sm" variant="ghost" className={`h-7 text-xs ${validation.errors.length > 0 ? 'text-red-400' : validation.warnings.length > 0 ? 'text-amber-400' : 'text-[#22c55e]'}`} onClick={() => setShowValidation(!showValidation)} data-testid="validation-btn">
          {validation.errors.length > 0 ? <AlertTriangle className="w-3 h-3 mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
          {validation.errors.length > 0 ? `${validation.errors.length} error(s)` : validation.warnings.length > 0 ? `${validation.warnings.length} warning(s)` : 'Valid'}
        </Button>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={undo} disabled={historyIdx < 0}>Undo</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={autoLayout}><LayoutGrid className="w-3 h-3 mr-1" />Layout</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs text-cyan-400" onClick={() => { setShowTest(!showTest); setSelectedNode(null); }} data-testid="test-flow-btn"><FlaskConical className="w-3 h-3 mr-1" />Test</Button>
        {selectedNode && (
          <>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={duplicateSelectedNode}><Copy className="w-3 h-3 mr-1" />Dup</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400" onClick={deleteSelectedNode}><Trash2 className="w-3 h-3 mr-1" />Del</Button>
          </>
        )}
        {flowId && (
          <>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={handleDuplicate}><Copy className="w-3 h-3 mr-1" />Clone</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400" onClick={handleDeleteFlow}><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
          </>
        )}
        <div className="h-5 w-px bg-slate-700" />
        <Button size="sm" className="h-7 bg-[#22c55e] hover:bg-[#16a34a] text-white text-xs" onClick={handleSave} disabled={saving} data-testid="save-flow-btn">
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Validation warnings bar */}
      {showValidation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
        <div className="bg-slate-800 border-b border-slate-700 px-4 py-2 flex items-start gap-2" data-testid="validation-bar">
          <div className="flex-1 space-y-1">
            {validation.errors.map((e, i) => <p key={`e${i}`} className="text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3 shrink-0" />{e}</p>)}
            {validation.warnings.map((w, i) => <p key={`w${i}`} className="text-xs text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3 shrink-0" />{w}</p>)}
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500" onClick={() => setShowValidation(false)}><X className="w-3 h-3" /></Button>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-48 bg-[#0f172a] border-r border-slate-700 p-2 overflow-y-auto shrink-0" data-testid="node-sidebar">
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-2 px-1">Drag to canvas</p>
          {sidebarNodes.map((sn) => {
            const SIcon = sn.icon;
            return (
              <div key={sn.type} className="flex items-center gap-2 px-2 py-1.5 mb-1 rounded cursor-grab border border-transparent hover:border-slate-600 hover:bg-slate-800/50 transition-colors" draggable onDragStart={(e) => onDragStart(e, sn.type)} data-testid={`sidebar-node-${sn.type}`}>
                <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: sn.color }}><SIcon className="w-3.5 h-3.5 text-white" /></div>
                <span className="text-xs text-slate-300 truncate">{sn.label}</span>
              </div>
            );
          })}
        </div>

        {/* Canvas */}
        <div className="flex-1" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onConnect={onConnect} onNodeClick={onNodeClick} onPaneClick={onPaneClick}
            onDrop={onDrop} onDragOver={onDragOver} onInit={setReactFlowInstance}
            nodeTypes={nodeTypes} defaultEdgeOptions={defaultEdgeOptions}
            fitView snapToGrid snapGrid={[15, 15]} deleteKeyCode="Delete"
            className="bg-slate-900"
          >
            <Background color="#1e293b" gap={20} size={1} />
            <Controls className="!bg-slate-800 !border-slate-700 !rounded-lg [&>button]:!bg-slate-800 [&>button]:!border-slate-700 [&>button]:!text-slate-300 [&>button:hover]:!bg-slate-700" />
            <MiniMap nodeColor={(n) => (nodeColors[n.type] || nodeColors.text_message).bg} className="!bg-slate-800 !border-slate-700 !rounded-lg" maskColor="rgba(15,23,42,0.7)" />
            {nodes.length === 0 && (
              <Panel position="top-center">
                <div className="mt-20 text-center text-slate-500 select-none">
                  <Zap className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-lg font-medium">Drag nodes from the sidebar to get started</p>
                  <p className="text-sm mt-1">Start with a Trigger node, then add message steps</p>
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>

        {/* Right Panel - Properties or Test */}
        {showTest && <TestPanel nodes={nodes} edges={edges} onClose={() => setShowTest(false)} />}
        {selectedNodeFull && !showTest && (
          <PropertiesPanel node={selectedNodeFull} onUpdate={updateNodeData} onClose={() => setSelectedNode(null)} media={media} />
        )}
      </div>
    </div>
  );
}
